import React, { useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { Form, Modal } from 'react-bootstrap';
import { Button } from '@sumologic/yama-ds-components';

import { Formik } from 'formik';
import * as Yup from 'yup';

import User from '~/models/User';

import types from '~/modules/customFieldTypes';
import { translate } from '~/modules/helpers/lang';
import { toErrorMessage } from '~/modules/helpers/error';

import FormField from '~/containers/FormField/FormField';

import Translate from '~/components/Translate/Translate';
import HeaderModal from '~/components/HeaderModal/HeaderModal';

import {
    requiredFieldMessage,
    emailMessage
} from '~/pages/GeneralSettings/UserSettings/userFields';

import StickyAlertContext from '~/containers/StickyAlertManager/StickyAlertContext';

const ForgotPassword = ({ show, onHide }) => {
    const [email, setEmail] = useState('');
    const stickyAlertContext = useContext(StickyAlertContext);

    const emailField = [
        {
            id: 'mail',
            name: 'mail',
            label: translate('Mail'),
            required: true,
            type: types.EMAIL,
            validation: Yup.string()
                .required(requiredFieldMessage)
                .email(emailMessage)
        }
    ];

    const createFieldValidationSchema = useCallback(() => {
        const schema = emailField.reduce((schema, field) => {
            if (field.validation) {
                schema[field.id] = field.validation;
            }
            return schema;
        }, {});
        return Yup.object().shape(schema);
    }, [emailField]);

    const fieldValidationSchema = createFieldValidationSchema();

    /**
     * Show sticky login error
     * @param message
     * @author RN
     */
    const showStickyMessage = message => {
        stickyAlertContext.addStickyAlert({
            status: 'danger',
            title: <Translate message="Reset password" />,
            message: message
        });
    };

    const handleUserRecoverPassword = email => {
        User.sendMailRecoverPassword({ email: email })
            .then(() => {
                onHide();
            })
            .catch(exception => {
                if (APP_INFO.debug) {
                    console.debug(exception);
                }
                showStickyMessage(
                    toErrorMessage(
                        exception,
                        'An error occurred while sending email for reset password'
                    )
                );
            });
    };

    return (
        <Modal size="md" centered show={show} onHide={onHide}>
            <HeaderModal title={'Forgot password'} onHide={onHide} />
            <Formik
                validationSchema={fieldValidationSchema}
                initialValues={{}}
                enableReinitialize
                validateOnMount
            >
                {({ ...formProps }) => {
                    return (
                        <>
                            <Modal.Body className={'p-4'}>
                                {emailField.map(field => {
                                    const { id, required, label, type } = field;
                                    let { hint } = field;
                                    const value = formProps.values[id];
                                    const isTouched = !!formProps.touched[id];
                                    const errors = formProps.errors[id];
                                    const onChange = (id, value) => {
                                        formProps.handleChange({
                                            target: {
                                                id,
                                                value
                                            }
                                        });
                                        setEmail(value);
                                    };

                                    return (
                                        <Form.Group
                                            key={id}
                                            className={'form-group'}
                                        >
                                            <Form.Label
                                                required={required}
                                                htmlFor={field.id}
                                            >
                                                {label}
                                            </Form.Label>
                                            <FormField
                                                id={id}
                                                label={label}
                                                onChange={onChange}
                                                onBlur={formProps.handleBlur}
                                                type={type}
                                                value={value}
                                                isRequired={required}
                                                isTouched={isTouched}
                                                errors={errors}
                                            />
                                            {errors && (
                                                <Form.Control.Feedback type="invalid">
                                                    {errors}
                                                </Form.Control.Feedback>
                                            )}
                                            {hint && (
                                                <Form.Text className="text-muted">
                                                    {hint}
                                                </Form.Text>
                                            )}
                                        </Form.Group>
                                    );
                                })}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={onHide}
                                    className="me-3"
                                >
                                    <Translate message="Close" />
                                </Button>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    className="me-3"
                                    disabled={!formProps.isValid}
                                    onClick={() => {
                                        handleUserRecoverPassword(email);
                                        onHide();
                                    }}
                                >
                                    <Translate message="Send mail" />
                                </Button>
                            </Modal.Footer>
                        </>
                    );
                }}
            </Formik>
        </Modal>
    );
};

ForgotPassword.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func
};

export default ForgotPassword;
